/* eslint-disable @typescript-eslint/no-floating-promises */
// Package Imports
import React from 'react';
import { ReactKeycloakProvider } from '@react-keycloak/web';
import { createRoot } from 'react-dom/client';
// Project Imports
import keycloak from 'keycloak/keyCloak';
import App from 'App';
import 'i18n';
import TokenProvider from 'contexts/resource/ResourceContext';
import useLogger from 'core/utils/useLogger';
import axiosInstance from 'providers/axiosInstance/axiosInstance';
import SocketNotificationProvider from 'contexts/resource/socketNotificationContext/SocketNotificationContext';
import { REACT_APP_ENV } from 'config';
import 'assets/styles/field.css';
import 'assets/styles/notification.css';
import 'antd/dist/reset.css'; // Antd 5+

declare global {
  interface Window {
    REACT_APP_ENV: string;
    REACT_APP_KEYCLOAK_REALM: string;
    REACT_APP_KEYCLOAK_URL: string;
    REACT_APP_KEYCLOAK_CLIENT_ID: string;
    REACT_APP_API_URL: string;
    REACT_APP_API_URL_UNIFIED: string;
    REACT_APP_API_URL_MASTER: string;
    REACT_APP_USER_API_URL: string;
    REACT_APP_API_URL_ORG: string;
    REACT_APP_API_URL_ROLE: string;
    REACT_APP_SOCKET_URL: string;
    REACT_APP_INPUT_SOURCE_API_URL: string;
  }
}

const waitForWindowConfig = () =>
  new Promise((resolve) => {
    const check = () => {
      if (window.REACT_APP_ENV || process.env.REACT_APP_ENV) {
        resolve(true);
      } else {
        setTimeout(check, 50);
      }
    };
    check();
  });

waitForWindowConfig().then(() => {
  console.log('Config loaded:', {
    ENV: REACT_APP_ENV,
  });
  const keycloakInitOptions = {
    onLoad: 'login-required',
    silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
    pkceMethod: 'S256',
    checkLoginIframe: false,
    responseMode: 'query',
  };
  const eventLogger = (event: string) => {
    if (event === 'onAuthRefreshError') {
      keycloak.logout();
    }
  };

  const tokenLogger = (tokens: { token?: string }) => {
    if (tokens?.token) {
      axiosInstance.defaults.headers.common.Authorization = `Bearer ${tokens.token}`;
    } else {
      delete axiosInstance.defaults.headers.common.Authorization;
    }
  };

  const container = document.getElementById('root');
  const logger = useLogger();
  if (container) {
    const root = createRoot(container);
    root.render(
      <TokenProvider>
        <SocketNotificationProvider>
          <ReactKeycloakProvider
            authClient={keycloak}
            onEvent={eventLogger}
            onTokens={tokenLogger}
            initOptions={keycloakInitOptions}
          >
            <React.StrictMode>
              <React.Suspense fallback="loading">
                <App />
              </React.Suspense>
            </React.StrictMode>
          </ReactKeycloakProvider>
        </SocketNotificationProvider>
      </TokenProvider>
    );
  } else {
    logger('Root element with id', '');
  }
});
